import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { ConfigurationAlertType, DType, DTypeCol, NextLocator } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { cloneDeep, merge } from 'lodash-es';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { TaskTypeService } from '../task-type.service';
import { NextTaskType, TaskTypeTypology } from '../task-type.types';
import { ConfirmChanges, UserNodePrivileges } from '../../../modules/admin/projects/projects.types';
import { NextServicio, ServiceTypology } from '../../../modules/admin/services/services.types';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { DTypeService } from '../../dtype/dtype.service';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation/confirmation.service';
import { LocalizePipe } from '../../pipes/localize.pipe';
import { ProjectsService } from '../../../modules/admin/projects/projects.service';
import { ServicesService } from '../../../modules/admin/services/services.service';
import { UserService } from '../../user/user.service';

@Component({
    selector: 'task-type',
    templateUrl: './task-type.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskTypeComponent implements ConfirmChanges, OnInit, OnDestroy {

    taskType: NextTaskType;
    service: NextServicio;

    uri: string;
    locator: NextLocator;
    queryParams: Params;
    role: UserNodePrivileges;

    dtypeForm: DTypeForm;
    isNew = false;
    hasChanges = false;
    loading = true;

    private _unsubscribeAll = new Subject<void>();

    constructor(
        private activatedRoute: ActivatedRoute,
        private _changeDetectorRef: ChangeDetectorRef,
        private dialogsService: DialogsService,
        private dtypeService: DTypeService,
        private _fuseConfirmationService: FuseConfirmationService,
        private localize: LocalizePipe,
        private router: Router,
        private _snackbar: MatSnackBar,
        private projectsService: ProjectsService,
        private serviceService: ServicesService,
        private taskTypeService: TaskTypeService,
        private translocoService: TranslocoService,
        private userService: UserService
    ) { }

    ngOnInit() {
        this.activatedRoute.paramMap
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async (params) => {
                // Viene de otra pagina de design con cambios
                if (this.hasChanges && params && params.get('tasktypeid') !== this.taskType.taskTypeId) {
                    if (!await this.confirm(null, null, this.translocoService.translate('exitWithoutSaving'))) {
                        this.router.navigate(['../design'], {
                            relativeTo: this.activatedRoute,
                            queryParams: { id: this.taskType.taskTypeId }
                        });
                        return;
                    }
                } else if (params && this.taskType && params.get('tasktypeid') === this.taskType.taskTypeId) {
                    return;
                }

                this.hasChanges = false;
                this.loading = true;

                this.queryParams = await firstValueFrom(this.activatedRoute.queryParams);
                this.locator = this.taskTypeService.getLocatorFromString(this.queryParams.locator);

                this.role = await this.projectsService.getRoleByLocators(this.queryParams);
                if (!this.hasPermissionToDesignRepository()) {
                    this.router.navigateByUrl('/403');
                }

                if (params.get('tasktypeid')) {
                    this.taskType = await firstValueFrom(this.taskTypeService.retrieveTaskTypeById(params.get('tasktypeid')));
                    if (!this.taskType.dtype) {
                        this.taskType.dtype = { column: [], path: [], uri: 'taskTypeExtra://' } as DType;
                    }
                } else {
                    this.isNew = true;

                    const service = await firstValueFrom(this.serviceService.service$);
                    let typology: TaskTypeTypology;
                    if (service.typology === ServiceTypology.WORKPLACES) {
                        typology = TaskTypeTypology.workshift;
                    } else {
                        // New instance taskType
                        const dialog = this.dialogsService.openNewTasktypeDataDialog();
                        typology = await firstValueFrom(dialog.afterClosed());
                    }
                    if (typology) {
                        //const reply = await firstValueFrom(this.taskTypeService.retrieveNewTaskTypeInstance({
                        //    typology: typology
                        //}));
                        if (typology === TaskTypeTypology.workshift) {
                            this.taskType = { forms: [], typology } as NextTaskType;
                        } else {
                            this.taskType = { forms: [], goals: [], typology, dtype: { column: [], path: [], uri: 'taskTypeExtra://' } } as NextTaskType;
                        }
                        //this.taskType.forms = [];
                        this.warn(this.translocoService.translate('warning'), this.translocoService.translate('warningMasterData'));
                    } else {
                        this.goBack();
                        return;
                    }
                }

                this.dtypeForm = await this.taskTypeService.retrieveTaskTypeDtypeForm(this.taskType, this.locator, { isUpdate: !this.isNew });

                this.dtypeForm.control.valueChanges
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(() => this.hasChanges = true);

                this._changeDetectorRef.detectChanges();
                this.loading = false;
            });
    }

    ngOnDestroy() {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    async save(): Promise<void> {
        const savedTaskType = await this.saveChanges();
        if (savedTaskType) {
            if (this.isNew) {
                this.router.navigate(['../' + savedTaskType.taskTypeId], { queryParams: this.queryParams, relativeTo: this.activatedRoute });
            }
            this.taskTypeService.emitUpdateSidebarEvent();
        }
    }

    cancel(): void {
        this.goBack();
    }

    deleteTaskType() {
        this.dtypeService.deleteNextDType(this.taskType?.uri).subscribe({
            next: (reply) => {
                if (reply?.status === 200) {
                    this._snackbar.open(this.translocoService.translate('deletedDType',
                        { name: this.taskType?.name }), this.translocoService.translate('accept'), { duration: 5000 });
                    this.goBack();
                } else {
                    this.dialogsService.openErrorDialog();
                }
            }, error: (err) => {
                console.error(err);
                this.dialogsService.openErrorDialog({
                    message: err?.error?.echo ? this.localize.transform(err?.error?.echo) : undefined,
                });
            }
        });
    }

    changedDType(event: { columns: DTypeCol[]; dtype: DType }) {
        if (event.dtype) {
            this.taskType.dtype = event.dtype;
        }
    }

    private hasPermissionToDesignRepository() {
        return this.projectsService.hasPermissionToDesignRepository(this.role, 'tasktype');
    }

    private async saveChanges() {
        const confirm = await this.confirm(this.translocoService.translate('warning'), this.translocoService.translate('saveTaskTypeConfirm'));
        if (confirm) {
            this.loading = true;
            this.dtypeForm.control.markAllAsTouched();
            if (!this.dtypeForm.control.valid) {
                this.loading = false;
                this.dialogsService.openErrorDialog({
                    message: this.translocoService.translate('errorsInForm')
                });
                return;
            }
            try {
                const tt = cloneDeep(this.taskType);
                tt.forms = tt.forms.filter(f => f.deprecated !== true);
                for (const f of tt.forms) {
                    delete f.form;
                }
                for (const g of tt.goals ?? []) {
                    delete g.form;
                }
                const taskType = await firstValueFrom(this.taskTypeService.flushTaskType(Object.assign(tt, this.dtypeForm.control.getRawValue()), this.locator));
                if (taskType) {
                    for (const f of taskType.forms) {
                        f.form = await this.taskTypeService.retrieveTaskTypeFormDTypeForm(f, this.locator, { isUpdate: true });
                    }
                    this.updateTaskType(taskType);
                    this._snackbar.open(this.translocoService.translate('taskTypeSaved', { name: taskType.name }), this.translocoService.translate('accept'), { duration: 5000 });
                    this.hasChanges = false;
                    this.loading = false;
                    return taskType;
                } else {
                    this._snackbar.open(this.translocoService.translate('somethingWentWrong'), this.translocoService.translate('accept'), { duration: 5000 });
                    this.loading = false;
                    return;
                }
            } catch (error) {
                console.error(error);
                this.loading = false;
                this.dialogsService.openErrorDialog({
                    message: error?.echo && Object.keys(error.echo).length > 0 ? this.localize.transform(error.echo) : undefined
                });
            }
        }
    }

    private updateTaskType(taskType: NextTaskType) {
        let control: any; //TODO actualizar libreria a angular 14
        for (const key of Object.keys(taskType)) {
            control = this.dtypeForm.control.get(key);
            if (control) {
                control.patchValue(taskType[key]);
            }
        }
    }

    private goBack() {
        if (!this.queryParams.marca_code) {
            this.router.navigate(['../'], { queryParams: {ou: '01'}, relativeTo: this.activatedRoute });
        } else {
            this.router.navigate(['../'], { queryParams: this.queryParams, relativeTo: this.activatedRoute });
        }
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string): Promise<boolean> {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.translocoService.translate('unsavedChanges'),
            message: message ? message : this.translocoService.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.translocoService.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.translocoService.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

    private warn(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.translocoService.translate('unsavedChanges'),
            message: message ? message : this.translocoService.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.translocoService.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.translocoService.translate('cancel'),
                    show: false
                }
            }
        });
    }
}
